import { createPackageManager, getRepoType } from '@wix/package-manager';
import {
  isStandaloneExtensionsProject,
  importExtensionsPackageNodeApi,
} from '@wix/cli-custom-extensions';
import type { ProjectModel } from '../../../model.js';
import {
  EVENT_LINE,
  EVENT_SERVER_ADDRESS,
  runAstroDev,
} from './runAstroDev.js';

export interface DevServerController {
  ready: Promise<string>;
  stop: () => Promise<void>;
}

interface DevServerLogger {
  log: (line: string) => void;
  error: (line: string) => void;
}

interface StartDevServerOptions {
  model: ProjectModel;
  port?: number;
  allowedHosts?: string;
  logger: DevServerLogger;
  onUnexpectedExit: () => void;
}

export async function startDevServer({
  model: { projectFolder },
  port,
  allowedHosts,
  logger,
  onUnexpectedExit,
}: StartDevServerOptions): Promise<DevServerController> {
  if (await isStandaloneExtensionsProject(projectFolder)) {
    return startStandaloneExtensionsDevServer({
      projectFolder,
      port,
      allowedHosts,
    });
  }

  return startAstroDevServer({
    projectFolder,
    port,
    allowedHosts,
    logger,
    onUnexpectedExit,
  });
}

async function startStandaloneExtensionsDevServer({
  projectFolder,
  port,
  allowedHosts,
}: {
  projectFolder: string;
  allowedHosts?: string;
  port?: number;
}): Promise<DevServerController> {
  const { dev } = await importExtensionsPackageNodeApi(projectFolder);
  const server = await dev({
    rootDir: projectFolder,
    port,
    allowedHosts: allowedHosts?.split(',').map((host) => host.trim()),
  });

  return {
    ready: Promise.resolve(`http://localhost:${server.address.port}`),
    stop: () => server.stop().catch(() => {}),
  };
}

async function startAstroDevServer({
  projectFolder,
  port,
  allowedHosts,
  logger,
  onUnexpectedExit,
}: {
  projectFolder: string;
  port?: number;
  allowedHosts?: string;
  logger: DevServerLogger;
  onUnexpectedExit: () => void;
}): Promise<DevServerController> {
  const repoType = await getRepoType(projectFolder);
  const packageManager = await createPackageManager(repoType);

  const astro = runAstroDev({
    packageManager,
    projectFolder,
    port,
    allowedHosts,
  });

  const ready = new Promise<string>((resolve) => {
    astro.stdout?.once(EVENT_SERVER_ADDRESS, (address: string) => {
      resolve(address);
    });
  });

  astro.stdout?.on(EVENT_LINE, (line: string) => logger.log(line));
  astro.stderr?.on(EVENT_LINE, (line: string) => logger.error(line));

  let stoppedByUs = false;
  let cleanedUp = false;

  const cleanupAstroProcess = () => {
    if (cleanedUp) {
      return;
    }

    cleanedUp = true;
    astro.stdout?.destroy();
    astro.stderr?.destroy();
    astro.stdin?.end();
  };

  void astro.on('exit', () => {
    cleanupAstroProcess();

    if (!stoppedByUs) {
      onUnexpectedExit();
    }
  });

  return {
    ready,
    stop: async () => {
      stoppedByUs = true;

      if (astro.pid) {
        try {
          astro.kill('SIGTERM');
        } catch {
          // The child process can be gone already if it was killed externally.
        }
      }

      cleanupAstroProcess();
    },
  };
}
